import React, { useState } from 'react'
import { columns } from './columns'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../lib/ui/table'
import { useTranslation } from '../../../hooks/useTranslation'
import { cn } from '../../../lib/utils'
import useSavedCourses from './SavedCoursesProvider'
import useFilters from '~/components/shared/useFilters'

export function DataTable() {
  const { t } = useTranslation('DataTable')
  const { courses } = useSavedCourses()

  const { columnFilters, setColumnFilters } = useFilters()

  const table = useReactTable({
    debugTable: true,
    data: courses,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: () => true,
    state: {
      columnFilters,
    },
    globalFilterFn: 'includesString',
    enableMultiSorting: true,
    getExpandedRowModel: getExpandedRowModel(),
    onColumnFiltersChange: setColumnFilters,
  })

  const [lastUpdatedId, setLastUpdatedId] = useState()

  const handleRowClick = (row) => () => {
    setLastUpdatedId(row.id)
  }

  return (
    <div className="rounded border border-alto-200 md:rounded-2xl">
      <Table tableClass="rounded md:rounded-2xl">
        <TableHeader className="rounded bg-intoo-blue-medium md:rounded-2xl">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={`${headerGroup.id}-header_group`}
              className="border-alto-200 bg-intoo-blue-medium p-4 text-white  hover:bg-intoo-blue-medium sm:px-8 sm:py-6"
            >
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={`${header.id}-header`}
                    className="job-search-table-header h-6 pl-0 text-sm font-bold text-foreground data-[col-id=status]:w-[96px] sm:h-12 sm:px-4 data-[col-id=status]:sm:w-[120px]"
                    data-col-id={header.column.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table
              .getRowModel()
              .rows.map((row) => (
                <DataTableRow
                  key={`${row.id}-table_row`}
                  row={row}
                  lastUpdatedId={lastUpdatedId}
                  onClick={handleRowClick(row)}
                />
              ))
          ) : (
            <TableRow className="p-4 sm:px-8 sm:py-6">
              <TableCell colSpan={columns.length + 2} className="h-24">
                <div className="flex flex-col items-center justify-center">
                  <div className="max-w-96 text-center">
                    <h2 className="p-8">{t('no_saved_courses_title')}</h2>
                    <p className="text-lg">{t('no_saved_courses_message')}</p>
                    <p className="text-lg">
                      <a href="/research_learning/learn">
                        {t('explore_courses')}
                      </a>
                    </p>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

const DataTableRow = ({ row, lastUpdatedId, ...props }) => {
  return (
    <>
      <TableRow
        data-state={row.getIsSelected() && 'selected'}
        fred={5}
        className={
          'border-alto-200 hover:bg-transparent data-[last-updated=true]:bg-alto-50'
        }
        data-last-updated={row.id === lastUpdatedId}
        {...props}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={`${row.id}_${cell.id}`}
            className="pb-2 pl-0 pr-0 pt-3 sm:p-4 sm:pr-1"
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
      <TableRow className={cn('border-b-0 p-0')}>
        <TableCell
          colSpan={columns.length + 2}
          className={cn('border-b-0 p-0')}
        >
          <div
            className={cn(
              'invisible max-h-0 overflow-hidden bg-[#f7f7f7] transition-all duration-500',
            )}
          ></div>
        </TableCell>
      </TableRow>
    </>
  )
}
